import { cx } from '@/styles/muiTheme';

export type ClipEventStyle = {
  /** Pill background. */
  bg: string;
  /** Pill foreground — the gold/green pills need dark text to stay legible. */
  fg: string;
  /** Filter-chip dot, a lighter sibling of `bg`. */
  dot: string;
};

/**
 * Colours for the event types the design names. `clipGroup.eventType` is
 * free-form across four leagues, so anything unnamed falls through to the
 * neutral style rather than being assigned a colour it does not own.
 */
const EVENT_STYLES: Record<string, ClipEventStyle> = {
  goal: { bg: cx.red.deep, fg: cx.text.primary, dot: cx.red.main },
  shot: { bg: cx.blue.main, fg: cx.text.primary, dot: cx.blue.hover },
  penalty: { bg: cx.gold, fg: cx.text.onLight, dot: cx.gold },
  hit: { bg: cx.bg.elevated, fg: cx.text.primary, dot: cx.text.tertiary },
  faceoff: { bg: cx.bg.elevated, fg: cx.text.primary, dot: cx.text.secondary },
  custom: { bg: cx.green, fg: cx.text.onLight, dot: cx.green },
};

const NEUTRAL_STYLE: ClipEventStyle = {
  bg: cx.bg.elevated,
  fg: cx.text.primary,
  dot: cx.text.tertiary,
};

/**
 * Event types arrive as underscore-joined tokens ("goal", "shootout_goal",
 * "basket_made_2pt"). Matching on the tokens rather than the whole string lets
 * "shootout_goal" inherit the goal colour, while a substring match would also
 * have to guard against accidental hits inside unrelated words.
 */
export const getEventStyle = (eventKey: string): ClipEventStyle => {
  const tokens = eventKey.toLowerCase().split('_');
  const match = tokens.find((token) => token in EVENT_STYLES);
  return match ? EVENT_STYLES[match] : NEUTRAL_STYLE;
};

/** "shootout_goal" -> "Shootout Goal". */
export const getEventLabel = (eventKey: string): string =>
  eventKey
    .split('_')
    .filter(Boolean)
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ');
